"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { FaCheckCircle, FaTimes, FaCheck, FaSignInAlt } from "react-icons/fa"
import { authService } from '../../../services/auth'
import { API_BASE_URL } from '../../../config/api'
import '../css/Landing.css';
import AnimatedEventCarousel from './AnimatedEventCarousel';
import BuzzTicketLogo from '../../../assets/BuzzTicketLogo.png';

import LoginPopup from '../../../components/LoginPopup'
import { fixS3ImageUrl } from '../../../components/imageUtils'
import BeeHiveModal from '../../../components/BeeHiveModal'
import { isValidEmail } from '../../../utils/validationUtils'

const getBackgroundStyle = (rawBackground) => {
  if (!rawBackground) {
    return {}
  }

  let background = rawBackground

  if (!Array.isArray(background) && typeof background === 'string') {
    const trimmed = background.trim()

    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        background = parsed
      }
    } catch {
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const inner = trimmed.slice(1, -1)
        const parts = inner.split(',')
        if (parts.length > 0) {
          background = parts.map((p) => {
            const t = p.trim()
            if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
              return t.slice(1, -1)
            }
            return t
          })
        }
      }
    }
  }

  if (!Array.isArray(background) || background.length === 0) {
    return {}
  }

  const mode = background[0]
  const value1 = background[1]
  const value2 = background[2]

  if (mode === 'solid' && value1) {
    return {
      backgroundColor: value1,
      backgroundImage: 'none',
    }
  }

  if (mode === 'gradient' && value1 && value2) {
    return {
      backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
      backgroundColor: 'transparent',
    }
  }

  if (mode === 'image' && value1) {
    const fixedBgUrl = fixS3ImageUrl(value1)
    return {
      backgroundImage: `url(${fixedBgUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    }
  }

  return {}
}

function Landing({ isLogin: initialIsLogin = false }) {
  const [isLogin, setIsLogin] = useState(initialIsLogin)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [topEvents, setTopEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showLoginToggle, setShowLoginToggle] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showBeeHiveModal, setShowBeeHiveModal] = useState(false)
  const nav = useNavigate()
  
  // Clear any existing authentication data when landing page loads
  useEffect(() => {
    authService.clearAuthData();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!isValidEmail(username)) {
      setErrorMessage('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      const result = await authService.login(username, password);
      
      if (result.success) {
        // Clear fields on successful login
        setUsername("");
        setPassword("");
        setShowLoginPopup(false);

        // Check if profile is complete
        if (result.profile_complete === false) {
          // Redirect to registration page to complete profile
          nav("/information-form");
        } else {
          // Profile is complete, redirect to appropriate dashboard
          const redirectPath = authService.getRedirectPath(result.data.role || (result.data.user && result.data.user.role));
          nav(redirectPath);
        }
      }
    } catch (error) {
      setErrorMessage(error.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setIsLogin(!isLogin)
    setErrorMessage("")
    // Preserve typed values when switching modes
  }

  const handleRegister = async (e, consent = {}) => {
    e.preventDefault()

    if (!username || !password) {
      setErrorMessage('Email and password are required.');
      return
    }

    if (!isValidEmail(username)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true)
      setErrorMessage('')

      const response = await fetch(`${API_BASE_URL}/shared/basic-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: username,
          password: password,
          termsAccepted: consent.termsAccepted === true,
          privacyAccepted: consent.privacyAccepted === true
        })
      })

      const result = await response.json()

      if (result.success) {
        setUsername('')
        setPassword('')
        setErrorMessage('')
        setShowLoginPopup(false)
        
        // Show success popup
        setSuccessMessage('Account created successfully! You can now login with your credentials.')
        setShowSuccessPopup(true)
      } else {
        setErrorMessage(result.error || 'Registration failed. Please try again.')
      }
    } catch (err) {
      setErrorMessage('Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const isRegisterButtonDisabled = !username || !password

  // Explicit reset that preserves when toggling/closing but can be invoked manually
  const handleResetAuthFields = () => {
    setUsername("");
    setPassword("");
    setErrorMessage("");
  }

  // Open popup in login mode from header button (no "Don't have an account" toggle)
  const handleOpenLogin = () => {
    setIsLogin(true)
    setShowLoginToggle(true)
    setShowLoginPopup(true)
  }

  // Open popup in register mode from footer quick link (with toggle back to login)
  const handleOpenRegister = () => {
    setIsLogin(false)
    setShowLoginToggle(true)
    setShowLoginPopup(true)
  }

  // Fetch top 6 events by capacity on component mount
  useEffect(() => {
    const fetchTopEvents = async () => {
      try {
        setLoadingEvents(true)
        console.log("📡 Fetching top capacity events for landing page")
        const response = await fetch(`${API_BASE_URL}/shared/events/top-capacity`)
        console.log("📡 Response status:", response.status)

        if (response.ok) {
          let events = await response.json()
          console.log("✅ Fetched top events:", events)
          console.log("Number of events fetched:", events.length)

          // Normalise events so Landing EventCard can use the same image/background
          // logic as the organiser HostRequest and Rehost cards
          const normalisedEvents = Array.isArray(events)
            ? events.map(event => {
                const capacity = event.capacity ?? event.total_tickets_planned ?? 0
                const sold = event.sold_tickets ?? 0
                const available = Math.max(0, capacity - sold)

                return {
                  ...event,
                  image_url: event.file_url || event.image_url || event.coverimage || null,
                  file_url: event.file_url || event.image_url || event.coverimage || null,
                  background: event.background_images_array || null,
                  capacity,
                  sold_tickets: sold,
                  available_capacity: available,
                }
              })
            : []

          setTopEvents(normalisedEvents)
        } else {
          console.error("❌ Failed to fetch top events, status:", response.status)
        }
      } catch (error) {
        console.error("❌ Error fetching top events:", error)
      } finally {
        setLoadingEvents(false)
      }
    }

    fetchTopEvents()
  }, [])

  return (
    <div className="landing-container">
      <button className="buzz-in-btn buzz-in-floating" onClick={handleOpenLogin}>
        <FaSignInAlt />
        <span>Login</span>
      </button>
      {showBeeHiveModal && (
        <BeeHiveModal
          events={topEvents}
          onClose={() => setShowBeeHiveModal(false)}
        />
      )}
      <div className="content-wrapper">
        <img src={BuzzTicketLogo} alt="BuzzTicketLogo" className="logo" />
        {/* Hero Section - 2 Column Layout */}
        <div className="hero-section">
          <div className="hero-left">
            <h1 className="hero-title">
              Create your <span className="hero-highlight">own</span>
              <span className="hero-title-line-2">hive of <span className="hero-highlight">events</span></span>
            </h1>
            <p className="hero-subtitle">
              Press the <span className="hero-highlight">Login Button</span> to sign up.
            </p>
            <button
              className="hero-primary-btn"
              onClick={() => setShowBeeHiveModal(true)}
            >
              Browse Our Buzzing Events
            </button>
            <div className="hero-features">
              <div className="hero-feature">
                <FaCheck className="feature-icon" />
                <span>Easy to use</span>
              </div>
              <div className="hero-feature">
                <FaCheck className="feature-icon" />
                <span>Secure payments</span>
              </div>
              <div className="hero-feature">
                <FaCheck className="feature-icon" />
                <span>Real-time insights</span>
              </div>
            </div>
          </div>
          <AnimatedEventCarousel
            topEvents={topEvents}
            loadingEvents={loadingEvents}
            nav={nav}
            getBackgroundStyle={getBackgroundStyle}
          />
        </div>

      </div>

      {/* Login Popup */}
      {showLoginPopup && (
        <LoginPopup
          onClose={() => setShowLoginPopup(false)}
          isLogin={isLogin}
          setIsLogin={setIsLogin}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          errorMessage={errorMessage}
          loading={loading}
          handleSubmit={handleSubmit}
          handleRegister={handleRegister}
          handleToggle={handleToggle}
          isRegisterButtonDisabled={isRegisterButtonDisabled}
          showLoginToggle={showLoginToggle}
          handleReset={handleResetAuthFields}
        />
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <button 
              className="success-popup-close"
              onClick={() => setShowSuccessPopup(false)}
            >
              <FaTimes />
            </button>
            
            <div className="success-popup-icon">
              <FaCheck />
            </div>
            
            <h3 className="success-popup-title">Account Created Successfully!</h3>
            
            <p className="success-popup-message">
              {successMessage}
            </p>
            
            <button 
              className="success-popup-button"
              onClick={() => setShowSuccessPopup(false)}
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Landing;
